import { formatPropertyAddress } from '../utils/propertyAddress';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import L from 'leaflet';
import { SITE_CONFIG } from '../config';
import { loadPropertyPage } from '../utils/boundedPropertySearch';
import { viewportBounds } from '../utils/boundedMapLayer';
import PropertyThumbnail from './PropertyThumbnail';
import { propertyMapIcon } from '../utils/mapMarkerIcons';
import './MapPropertyGrid.css';

export function propertyInViewport(property, bounds) {
  if (property.latitude == null || property.longitude == null) return false;
  const lat = Number(property.latitude);
  const lng = ((Number(property.longitude) + 180) % 360 + 360) % 360 - 180;
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= bounds.south && lat <= bounds.north &&
    (bounds.west <= bounds.east ? lng >= bounds.west && lng <= bounds.east : lng >= bounds.west || lng <= bounds.east);
}

export default function MapPropertyGrid({ map, params, properties, onProperty, favorites, onSave, trayExpanded = false, onTrayToggle }) {
  const resultsId = useId();
  const [state, setState] = useState({ homes: [], loading: true, error: '', hasMore: false });
  const requestRef = useRef(null);
  const scrollerRef = useRef(null);
  const highlightRef = useRef(null);
  const clearHighlight = useCallback(() => {
    highlightRef.current?.remove();
    highlightRef.current = null;
  }, []);
  const highlightHome = home => {
    clearHighlight();
    if (!map || home.latitude == null || home.longitude == null) return;
    const lat = Number(home.latitude), lng = Number(home.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const icon = propertyMapIcon(home.current_price ?? home.list_price ?? home.price, { isPrivate: home.isPrivate });
    icon.options.className += ' property-map-highlight';
    icon.options.html = '<span class="property-map-orbit" aria-hidden="true"></span>' + icon.options.html;
    // A separate noninteractive marker also reveals a home inside a cluster.
    highlightRef.current = L.marker([lat, lng], { icon, interactive: false, keyboard: false, zIndexOffset: 10000 }).addTo(map);
  };
  useEffect(() => clearHighlight, [map, clearHighlight]);
  // A stable search signature prevents map/status rerenders from restarting pagination.
  const searchSignature = JSON.stringify(params || {});

  useEffect(() => {
    if (!map || (!params && !properties)) return;
    let timer;
    const session = { disposed: false, generation: 0, controller: null, busy: false, pageState: null, homes: [], hasMore: false };
    const load = async () => {
      if (session.busy || session.disposed) return;
      session.busy = true;
      const generation = session.generation;
      const controller = new AbortController();
      session.controller = controller;
      setState(previous => ({ ...previous, loading: true, error: '' }));
      try {
        const result = await loadPropertyPage({
          params: { ...JSON.parse(searchSignature), ...session.bounds },
          pageState: session.pageState, pageSize: 48, signal: controller.signal,
          siteId: SITE_CONFIG.SITE_ID, privateApiKey: 'treas-api-key-2025', queryOptions: { firstPhoto: true }
        });
        if (session.disposed || generation !== session.generation) return;
        session.pageState = result.pageState;
        session.hasMore = result.hasMore;
        session.homes = [...session.homes, ...result.properties.filter(home => propertyInViewport(home, session.bounds))];
        setState({ homes: session.homes, loading: false, error: '', hasMore: session.hasMore });
      } catch (error) {
        if (!session.disposed && generation === session.generation && error.name !== 'AbortError') {
          setState(previous => ({ ...previous, loading: false, error: error.message || 'Unable to load homes. Please try again.' }));
        }
      } finally {
        if (generation === session.generation) session.busy = false;
      }
    };
    requestRef.current = load;
    const refresh = () => {
      clearHighlight();
      clearTimeout(timer);
      session.generation++;
      session.controller?.abort();
      session.busy = false;
      session.pageState = null;
      session.homes = [];
      session.bounds = viewportBounds(map);
      if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
      if (properties) {
        session.homes = properties.filter(home => propertyInViewport(home, session.bounds));
        setState({ homes: session.homes, loading: false, error: '', hasMore: false });
      } else {
        setState({ homes: [], loading: true, error: '', hasMore: false });
        timer = setTimeout(load, 350);
      }
    };
    map.on('moveend', refresh);
    refresh();
    return () => {
      session.disposed = true;
      session.controller?.abort();
      clearTimeout(timer);
      map.off('moveend', refresh);
      requestRef.current = null;
    };
  }, [map, searchSignature, properties, Boolean(params), clearHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  return <aside className="map-property-panel" aria-label="Properties in the visible map area">
    <button type="button" className="map-property-tray-toggle" onClick={onTrayToggle}
      aria-expanded={trayExpanded} aria-controls={resultsId} aria-label={trayExpanded ? 'Collapse homes tray' : 'Expand homes tray'}>
      <span className="map-property-tray-handle" aria-hidden="true" />
      <span className="map-property-tray-heading"><strong>Homes in view</strong><span>{trayExpanded ? 'Collapse ▾' : 'Expand ▴'}</span></span>
    </button>
    <header><strong>Homes in view</strong><span>Move the map to explore</span></header>
    <div id={resultsId} className="map-property-scroll" ref={scrollerRef} onScroll={event => {
      const node = event.currentTarget;
      if (state.hasMore && !state.loading && !state.error && node.scrollHeight - node.scrollTop - node.clientHeight < 200) requestRef.current?.();
    }}>
      <div className="map-property-grid">
        {state.homes.map(home => {
          const amount = home.current_price ?? home.list_price ?? home.price;
          const price = amount == null ? 'Price unavailable' : Number(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
          const address = formatPropertyAddress(home, { streetOnly: true }) || 'Address unavailable';
          const listingKey = String(home.listing_key ?? home.listingKey ?? '');
          const saved = favorites?.has(listingKey) || false;
          return <div key={listingKey || `${home.isPrivate ? 'private' : 'mls'}-${home.id}`} className="map-property-card"
            onMouseEnter={() => highlightHome(home)} onMouseLeave={clearHighlight}
            onFocus={() => highlightHome(home)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) clearHighlight(); }}>
            <button type="button" className="map-property-tile"
            aria-label={`View property ${address === 'Address unavailable' ? listingKey : address}, ${price}`} onClick={() => { clearHighlight(); onProperty(home); }}>
            <PropertyThumbnail listingKey={home.isPrivate ? undefined : home.listing_key} src={home.first_photo_url || home.image_url || home.photo_url} alt="Property" />
            <span className="map-property-caption"><span className="map-property-price">{price}</span><span className="map-property-address" title={address}>{address}</span></span>
            </button>
            <button type="button" className={`map-property-save${saved ? ' is-saved' : ''}`} aria-pressed={saved}
              aria-label={`${saved ? 'Unsave' : 'Save'} property ${address}`} disabled={!listingKey || !onSave}
              onClick={() => { clearHighlight(); onSave?.(home); }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" /></svg>
            </button>
          </div>;
        })}
      </div>
      {state.loading && <p role="status">Loading homes…</p>}
      {state.error && <div role="alert"><p>{state.error}</p><button type="button" onClick={() => requestRef.current?.()}>Try again</button></div>}
      {!state.loading && !state.error && !state.homes.length && <p>No homes in this map area. Move or zoom out to explore.</p>}
      {state.hasMore && !state.loading && !state.error && <button className="map-property-more" type="button" onClick={() => requestRef.current?.()}>Load more homes</button>}
    </div>
  </aside>;
}
